import {
    ArrowLeft,
    Camera,
    ImagePlus,
    Images,
    Mic,
    Search,
    ShoppingCart,
    X,
} from "lucide-react";
import {
    ChangeEvent,
    FormEvent,
    useEffect,
    useRef,
    useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../../Redux/hooks";
import { recordActivity } from "../../../../Redux/ActivityApi/slice";
import { ActivityType } from "../../../../Redux/ActivityApi/types";
import "./MobileDetailNavbar.css";

type MobileDetailNavbarProps = {
    cartCount?: number;
    cartPath?: string;
    fallbackPath?: string;
    searchPlaceholder?: string;
};

const normalizePath = (pathname: string) => {
    if (!pathname) return "/";
    return pathname.length > 1 ? pathname.replace(/\/+$/, "").toLowerCase() : pathname.toLowerCase();
};

const MobileDetailNavbar = ({
    cartCount = 0,
    cartPath = "/Cart",
    fallbackPath = "/HomePage",
    searchPlaceholder = "Search Salar",
}: MobileDetailNavbarProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const galleryInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const visualMenuRef = useRef<HTMLDivElement>(null);
    const [searchValue, setSearchValue] = useState("");
    const [visualMenuOpen, setVisualMenuOpen] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [utilityMessage, setUtilityMessage] = useState("");

    useEffect(() => {
        if (!visualMenuOpen) return;
        const handleOutsideClick = (event: PointerEvent) => {
            const target = event.target as Node;
            if (visualMenuRef.current && !visualMenuRef.current.contains(target)) {
                setVisualMenuOpen(false);
            }
        };
        window.addEventListener("pointerdown", handleOutsideClick);
        return () => window.removeEventListener("pointerdown", handleOutsideClick);
    }, [visualMenuOpen]);

    useEffect(() => {
        if (!utilityMessage) return;
        const timeout = window.setTimeout(() => setUtilityMessage(""), 2800);
        return () => window.clearTimeout(timeout);
    }, [utilityMessage]);

    const handleBack = () => {
        if (location.key && location.key !== "default") {
            navigate(-1);
            return;
        }
        navigate(fallbackPath, { replace: true });
    };

    const handleSearchSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const value = searchValue.trim();
        if (!value) {
            setUtilityMessage("Type something to search.");
            return;
        }
        await dispatch(recordActivity({
            activities: { eventType: ActivityType.SEARCH, searchQuery: value },
        }));
        navigate(`/Categories/All?q=${encodeURIComponent(value)}`);
    };

    const handleGallerySelect = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setVisualMenuOpen(false);
        setUtilityMessage(`Image selected: ${file.name}`);
        event.target.value = "";
    };

    const handleCameraSelect = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setVisualMenuOpen(false);
        setUtilityMessage("Camera image ready.");
        event.target.value = "";
    };

    const handleVoiceSearch = () => {
        const SpeechRecognition =
            (window as any).SpeechRecognition ||
            (window as any).webkitSpeechRecognition;
        if (!SpeechRecognition) {
            setUtilityMessage("Voice search is not supported on this browser.");
            return;
        }
        const recognition = new SpeechRecognition();
        recognition.lang = "en-IN";
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;
        recognition.onstart = () => { setIsListening(true); setUtilityMessage("Listening…"); };
        recognition.onresult = (event: any) => {
            const transcript = event.results?.[0]?.[0]?.transcript || "";
            if (!transcript) return;
            setSearchValue(transcript);
            setUtilityMessage(`Voice: ${transcript}`);
        };
        recognition.onerror = () => setUtilityMessage("Could not hear you. Try again.");
        recognition.onend = () => setIsListening(false);
        recognition.start();
    };

    const handleCartClick = () => {
        if (normalizePath(location.pathname) === normalizePath(cartPath)) return;
        navigate(cartPath);
    };

    return (
        <div className="salar-mobile-detail-nav-shell">
            <header className="salar-mobile-detail-nav">
                <div className="salar-mobile-detail-nav-glass" aria-hidden="true" />
                <button type="button" className="salar-mobile-detail-back" onClick={handleBack} aria-label="Go back">
                    <ArrowLeft size={22} strokeWidth={2.2} />
                </button>
                <form className="salar-mobile-detail-search" onSubmit={handleSearchSubmit}>
                    <Search className="salar-mobile-detail-search-icon" size={20} strokeWidth={2.1} />
                    <input
                        type="search"
                        value={searchValue}
                        onChange={(event) => setSearchValue(event.target.value)}
                        placeholder={searchPlaceholder}
                        aria-label="Search Salar"
                    />
                    {searchValue && (
                        <button
                            type="button"
                            className="salar-mobile-detail-search-action salar-mobile-detail-clear"
                            onClick={() => setSearchValue("")}
                            aria-label="Clear search"
                        >
                            <X size={16} strokeWidth={2.1} />
                        </button>
                    )}
                    <div ref={visualMenuRef} className="salar-mobile-detail-visual-wrap">
                        <button
                            type="button"
                            className={`salar-mobile-detail-search-action ${visualMenuOpen ? "active" : ""}`}
                            onClick={() => setVisualMenuOpen((c) => !c)}
                            aria-label="Search by image or camera"
                            aria-expanded={visualMenuOpen}
                        >
                            <ImagePlus size={19} strokeWidth={1.9} />
                        </button>
                        {visualMenuOpen && (
                            <div className="salar-mobile-detail-visual-menu">
                                <button type="button" onClick={() => galleryInputRef.current?.click()}>
                                    <span><Images size={18} strokeWidth={2} /></span>
                                    <div>
                                        <strong>Upload image</strong>
                                        <small>Choose from gallery</small>
                                    </div>
                                </button>
                                <button type="button" onClick={() => cameraInputRef.current?.click()}>
                                    <span><Camera size={18} strokeWidth={2} /></span>
                                    <div>
                                        <strong>Camera</strong>
                                        <small>Take a photo</small>
                                    </div>
                                </button>
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        className={`salar-mobile-detail-search-action salar-mobile-detail-mic ${isListening ? "listening" : ""}`}
                        onClick={handleVoiceSearch}
                        aria-label="Voice search"
                    >
                        <Mic size={19} strokeWidth={2} />
                    </button>
                </form>
                <button
                    type="button"
                    className="salar-mobile-detail-cart"
                    onClick={handleCartClick}
                    aria-label={`Cart with ${cartCount} items`}
                >
                    <ShoppingCart size={22} strokeWidth={2.05} />
                    {cartCount > 0 && (
                        <span className="salar-mobile-detail-cart-badge">
                            {cartCount > 99 ? "99+" : cartCount}
                        </span>
                    )}
                </button>
                <input ref={galleryInputRef} className="salar-mobile-detail-hidden-input" type="file" accept="image/*" onChange={handleGallerySelect} />
                <input ref={cameraInputRef} className="salar-mobile-detail-hidden-input" type="file" accept="image/*" capture="environment" onChange={handleCameraSelect} />
            </header>
            {utilityMessage && (
                <div className="salar-mobile-detail-message" aria-live="polite">
                    {utilityMessage}
                </div>
            )}
        </div>
    );
};

export default MobileDetailNavbar;
